import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
    GraduationCap,
    Music,
    Building,
    Briefcase,
    BookOpen,
    Users,
    CheckCircle,
    Award,
    ArrowLeft,
    Globe,
    Mail,
    Scale,
    Target,
    TrendingUp,
    Lightbulb,
    Brain,
    Shield,
    Star,
    Monitor,
    Loader2,
    BrainCircuit
} from 'lucide-react';

const LGACPostdoctorados = () => {
    const [programaData, setProgramaData] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    // Cargar datos desde JSON
    useEffect(() => {
        const loadProgramaData = async () => {
            try {
                setIsLoading(true);
                const response = await fetch(`/data/postdoctorados.json?v=${Date.now()}`);
                
                if (!response.ok) {
                    throw new Error('Error al cargar los datos del programa');
                }
                
                const data = await response.json();
                setProgramaData(data);
                setError(null);
            } catch (err) {
                console.error('Error cargando datos de postdoctorados:', err);
                setError(err.message);
            } finally {
                setIsLoading(false);
            }
        };

        loadProgramaData();
    }, []);

    // Mapeo de iconos desde strings a componentes de lucide-react
    const iconMap = {
        BrainCircuit,
        Building,
        Award,
        Monitor,
        Globe,
        Shield,
        Briefcase,
        BookOpen,
        Scale,
        Target,
        TrendingUp,
        Users,
        Lightbulb,
        Brain,
        Star,
        Music
    };

    // Cargar líneas completas de investigación desde JSON y mapear iconos
    const lineasBasicas = (programaData?.lineasInvestigacion || []).map(linea => ({
        ...linea,
        icono: iconMap[linea.icono] || BookOpen
    }));

    // Combinar líneas básicas con descripciones adicionales del JSON
    const lgacCompletas = lineasBasicas.map(linea => {
        const descripcionAdicional = programaData?.lgacDescripciones?.[linea.titulo] || {};
        return {
            ...linea,
            descripcion: descripcionAdicional.descripcion || `Las líneas de investigación del Postdoctorado en ${linea.titulo}.`,
            lineas: descripcionAdicional.lineasAdicionales || linea.lineas,
            coordinador: descripcionAdicional.coordinador,
            notaEspecial: descripcionAdicional.notaEspecial,
            destacado: descripcionAdicional.destacado || false
        };
    });

    // Mostrar estado de carga
    if (isLoading) {
        return (
            <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white flex items-center justify-center">
                <div className="text-center">
                    <Loader2 className="w-12 h-12 text-[#0b742f] animate-spin mx-auto mb-4" />
                    <p className="text-gray-600 text-lg">Cargando información del programa...</p>
                </div>
            </div>
        );
    }

    // Mostrar error si falla la carga
    if (error) {
        return (
            <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white flex items-center justify-center">
                <div className="text-center px-4">
                    <p className="text-red-600 font-semibold text-xl mb-2">Error al cargar la información</p>
                    <p className="text-red-500">{error}</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
            {/* Hero Section */}
            <div className="relative bg-gradient-to-r from-[#0b742f] to-[#085a24] text-white py-20 overflow-hidden">
                <div className="absolute inset-0 opacity-10">
                    <img
                        src="https://recursos.cenid.org/img_cenid/servicios/img_postdoctorado2.jpg"
                        alt="Background"
                        className="w-full h-full object-cover"
                    />
                </div>
                <div className="relative max-w-7xl mx-auto px-6">
                    <div className="flex items-center gap-4 mb-6">
                        <div className="bg-white/10 backdrop-blur-sm p-4 rounded-xl">
                            <BookOpen className="w-12 h-12" />
                        </div>
                        <div>
                            <h1 className="text-4xl md:text-5xl font-bold mb-2">
                                Líneas de Generación y Aplicación del Conocimiento
                            </h1>
                            <p className="text-xl text-green-100 flex items-center gap-2">
                                <Award className="w-5 h-5" />
                                Postdoctorados CENID - LGAC
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            <div className="max-w-7xl mx-auto px-6 py-16">
                {/* Navegación de regreso */}
                <div className="mb-8">
                    <Link
                        to="/postdoctorados"
                        className="inline-flex items-center gap-2 text-[#0b742f] hover:text-[#085a24] font-semibold transition-colors duration-300"
                    >
                        <ArrowLeft className="w-5 h-5" />
                        Regresar a Postdoctorados
                    </Link>
                </div>

                {/* Introducción */}
                <section className="mb-16">
                    <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12 border-l-4 border-[#0b742f]">
                        <div className="text-center mb-8">
                            <h2 className="text-3xl font-bold text-gray-900 mb-4">Desarrollo del programa</h2>
                            <p className="text-lg text-gray-700 max-w-4xl mx-auto">
                                El programa académico debe desarrollarse en un plazo de doce meses, incluyen programa académico,
                                metodología, seguimiento, asesoría académica y editorial y evaluaciones, los cuales son proporcionados
                                a partir del décimo día hábil una vez admitido.
                            </p>
                        </div>

                        {/* Diagrama de Etapas del Programa */}
                        <div className="mb-8">
                            <div className="grid md:grid-cols-2 gap-8">

                                {/* 1 Etapa */}
                                <div className="bg-gradient-to-br from-orange-50 to-yellow-50 rounded-2xl p-6 border border-orange-200">
                                    <div className="text-center mb-6">
                                        <h3 className="text-2xl font-bold text-blue-600 mb-2">1 Etapa</h3>
                                        <p className="text-gray-700 font-semibold">duración 6 meses</p>
                                    </div>

                                    <div className="space-y-3">
                                        <div className="bg-yellow-400 text-white px-4 py-3 rounded-lg text-center font-semibold text-sm">
                                            Taller: Presentación de proyecto postdoctoral I
                                        </div>

                                        <div className="bg-orange-500 text-white px-4 py-3 rounded-lg text-center font-semibold text-sm">
                                            Primer seminario de especialización
                                        </div>

                                        <div className="bg-green-400 text-white px-4 py-3 rounded-lg text-center font-semibold text-sm">
                                            Segundo seminario de especialización
                                        </div>

                                        <div className="bg-green-500 text-white px-4 py-3 rounded-lg text-center font-semibold text-sm">
                                            Taller: Presentación de proyecto postdoctoral II
                                        </div>

                                        <div className="bg-orange-600 text-white px-4 py-4 rounded-lg text-center text-sm leading-tight">
                                            <p className="font-semibold mb-2">Elaboración de protocolo; revisión y aprobación de tópicos, inscripción a Líneas de Generación y Aplicación del Conocimiento (LGAC), donde recibirá su carta de aceptación.</p>
                                            <p className="text-xs italic bg-orange-700 rounded px-2 py-1 mt-2">
                                                • Al final de la primera etapa se otorgará constancia de diplomado "Competencias para la Investigación multidisciplinaria"
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                {/* 2 Etapa */}
                                <div className="bg-gradient-to-br from-green-50 to-blue-50 rounded-2xl p-6 border border-green-200">
                                    <div className="text-center mb-6">
                                        <h3 className="text-2xl font-bold text-blue-600 mb-2">2 Etapa</h3>
                                        <p className="text-gray-700 font-semibold">duración 6 meses</p>
                                    </div>

                                    <div className="space-y-3">
                                        <div className="bg-green-500 text-white px-4 py-3 rounded-lg text-center font-semibold text-sm">
                                            Elaboración de plan de trabajo a desarrollar para la etapa de investigación.
                                        </div>

                                        <div className="bg-green-400 text-white px-4 py-4 rounded-lg text-center text-sm leading-tight">
                                            Estancia de investigación en modalidad virtual a través de la Red Intercontinental de Investigaciones sobre Educación en Línea o en modalidad presencial mediante carta aceptación en alguna institución educativa.
                                        </div>

                                        <div className="bg-teal-500 text-white px-4 py-3 rounded-lg text-center font-semibold text-sm">
                                            Memoria de plan de trabajo.
                                        </div>

                                        <div className="bg-cyan-500 text-white px-4 py-3 rounded-lg text-center font-semibold text-sm">
                                            Invitación como conferencista magistral en modalidad virtual.
                                        </div>

                                        <div className="bg-blue-400 text-white px-4 py-3 rounded-lg text-center font-semibold text-sm">
                                            Defensa en modalidad virtual ante el comité de expertos de su proyecto postdoctoral.
                                        </div>

                                        <div className="bg-blue-600 text-white px-4 py-3 rounded-lg text-center font-semibold text-sm">
                                            • Publicación académica arbitrada en formato electrónico.
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* LGAC Detalladas */}
                <section className="mb-16">
                    <div className="text-center mb-12">
                        <h2 className="text-3xl font-bold text-gray-900 mb-3">Líneas de Generación y Aplicación del Conocimiento</h2>
                        <div className="h-1 w-24 bg-[#e87729] rounded mx-auto mb-6"></div>
                        <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                            Conoce en detalle cada una de las líneas de investigación disponibles en nuestros programas postdoctorales
                        </p>
                    </div>

                    <div className="space-y-12">
                        {lgacCompletas.map((lgac, index) => {
                            const IconComponent = lgac.icono;
                            return (
                                <div
                                    key={index}
                                    className={`bg-white rounded-2xl shadow-xl overflow-hidden border ${lgac.destacado ? 'border-purple-200 ring-2 ring-purple-100' : 'border-gray-200'
                                        }`}
                                >
                                    {/* Header con gradiente */}
                                    <div className={`${lgac.color} p-8 text-white`}>
                                        <div className="flex items-center gap-4 mb-4">
                                            <div className="bg-white/20 p-4 rounded-xl">
                                                <IconComponent className="w-12 h-12" />
                                            </div>
                                            <div>
                                                <h3 className="text-3xl font-bold">{lgac.titulo}</h3>
                                                {lgac.destacado && (
                                                    <div className="flex items-center gap-2 mt-2">
                                                        <Award className="w-5 h-5" />
                                                        <span className="text-sm font-medium">Programa Internacional Destacado</span>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Contenido */}
                                    <div className="p-8">
                                        {/* Descripción */}
                                        <div className="mb-8">
                                            <h4 className="text-xl font-bold text-gray-900 mb-4">Líneas de investigación</h4>
                                            <p className="text-gray-700 leading-relaxed mb-6">{lgac.descripcion}</p>

                                            <div className="grid md:grid-cols-2 gap-4">
                                                {lgac.lineas.map((linea, idx) => (
                                                    <div key={idx} className="flex items-start gap-3 bg-gray-50 rounded-lg p-4">
                                                        <CheckCircle className="w-5 h-5 text-[#0b742f] mt-0.5 flex-shrink-0" />
                                                        <span className="text-gray-700 font-medium">{linea}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>

                                        {/* Nota especial */}
                                        {lgac.notaEspecial && (
                                            <div className="bg-blue-50 border-l-4 border-blue-400 p-6 mb-8">
                                                <p className="text-blue-800 font-medium leading-relaxed">{lgac.notaEspecial}</p>
                                            </div>
                                        )}

                                        {/* Coordinador */}
                                        {lgac.coordinador && (
                                            <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-8">
                                                <h4 className="text-2xl font-bold text-[#0b742f] mb-6 flex items-center gap-3">
                                                    <Users className="w-8 h-8" />
                                                    Coordinador del Postdoctorado Internacional en Estudios Musicales y Musicológicos
                                                </h4>

                                                <div className="space-y-6">
                                                    <div className="bg-white rounded-lg p-6 shadow-sm">
                                                        <h5 className="text-xl font-bold text-purple-700 mb-4">{lgac.coordinador.nombre}</h5>
                                                        <p className="text-gray-700 leading-relaxed">{lgac.coordinador.descripcion}</p>
                                                    </div>

                                                    {lgac.coordinador.descripcionExtendida && (
                                                        <div className="bg-white rounded-lg p-6 shadow-sm">
                                                            <h6 className="text-lg font-semibold text-gray-900 mb-3">Trayectoria académica y profesional</h6>
                                                            <p className="text-gray-700 leading-relaxed">
                                                                {lgac.coordinador.descripcionExtendida}
                                                            </p>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </section>

                {/* Llamada a la acción */}
                <section className="mb-16">
                    <div className="bg-gradient-to-r from-[#0b742f] to-[#085a24] text-white rounded-2xl shadow-xl p-8 md:p-12 text-center">
                        <h2 className="text-3xl font-bold text-white mb-4">¿Interesado en alguna LGAC?</h2>
                        <p className="text-xl text-green-100 mb-8">
                            Contacta con nosotros para más información sobre las líneas de investigación disponibles
                        </p>
                        <div className="flex flex-col sm:flex-row gap-4 justify-center">
                            <a
                                href="mailto:postdoctoradoscenid@cenid.org"
                                className="inline-flex items-center gap-2 bg-white text-[#0b742f] px-8 py-3 rounded-lg font-bold hover:bg-gray-100 transition-colors duration-300"
                            >
                                <Mail className="w-5 h-5" />
                                Contactar por Email
                            </a>
                            <Link
                                to="/postdoctorados"
                                className="inline-flex items-center gap-2 bg-[#e87729] text-white px-8 py-3 rounded-lg font-bold hover:bg-[#d66820] transition-colors duration-300"
                            >
                                <Globe className="w-5 h-5" />
                                Ver Programa Completo
                            </Link>
                        </div>
                    </div>
                </section>
            </div>
        </div>
    );
};

export default LGACPostdoctorados;